import React, { useState, useEffect } from "react";
import { assetAPI, usersAPI, formatError } from "../utils/api";
import { useKitchen } from "../contexts/KitchenContext";
import { Plus, Edit, Trash2, X, Search, AlertTriangle, CheckCircle, Wrench, Download } from "lucide-react";
import { exportCSV } from "../utils/csvUtils";
import { useConfirm } from "../hooks/useConfirm";

const CATEGORIES = ["Machinery", "Equipment", "Vehicle", "Furniture", "Electronics", "Other"];

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto animate-fade-in-up">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB] sticky top-0 bg-white">
          <h2 className="font-bold text-lg font-[Manrope] text-[#3A322C]">{title}</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

const TABS = [
  { id: "register", label: "Asset Register" },
  { id: "alerts", label: "Warranty & Service Alerts" },
];

export default function AssetManagement() {
  const { kitchens: ctxKitchens } = useKitchen();
  const { confirm, ConfirmModal } = useConfirm();
  const [kitchens, setKitchens] = useState(ctxKitchens || []);
  // Fallback: direct fetch if context kitchens still empty
  useEffect(() => {
    if (ctxKitchens && ctxKitchens.length > 0) { setKitchens(ctxKitchens); return; }
    const _t = setTimeout(() => {
      if (kitchens.length > 0) return;
      import("../utils/api").then(({ usersAPI }) => {
        usersAPI.getKitchens().then(r => {
          const ks = r.data || [];
          if (ks.length > 0) setKitchens(ks);
        }).catch(() => {});
      });
    }, 800);
    return () => clearTimeout(_t);
  }, [ctxKitchens]);

  const [activeTab, setActiveTab] = useState("register");
  const [assets, setAssets] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [filterKitchen, setFilterKitchen] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    usersAPI.getKitchens().then(r => {
      const ks = r.data || [];
      if (ks.length > 0) setFilterKitchen(ks[0].id);
    });
  }, []);

  useEffect(() => { fetchData(); }, [activeTab, filterKitchen, filterCategory, search]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterKitchen) params.kitchen_id = filterKitchen;
      if (filterCategory) params.category = filterCategory;
      if (search) params.search = search;
      if (activeTab === "register") {
        const r = await assetAPI.getAssets(params);
        setAssets(r.data || []);
      } else {
        const r = await assetAPI.getAlerts({ kitchen_id: filterKitchen });
        setAlerts(r.data || []);
      }
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  };

  const handleSave = async () => {
    setError("");
    try {
      if (modal.mode === "create") await assetAPI.createAsset({ ...form, kitchen_id: filterKitchen });
      else await assetAPI.updateAsset(modal.id, form);
      setModal(null);
      setSuccess("Saved!"); setTimeout(() => setSuccess(""), 2000);
      fetchData();
    } catch (e) { setError(formatError(e)); }
  };

  const handleDelete = async (id) => {
    if (!await confirm("Delete asset?")) return;
    await assetAPI.deleteAsset(id);
    fetchData();
  };

  const kName = (id) => kitchens.find(k => k.id === id)?.name || "—";

  return (
    <div className="space-y-6 max-w-6xl" data-testid="asset-management">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C]">Asset Management</h1>
          <p className="text-sm text-[#8A7E73] mt-0.5">Kitchen-wise machinery, equipment & warranty tracking</p>
        </div>
        <div className="flex items-center gap-2">
          <select className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
            value={filterKitchen} onChange={e => setFilterKitchen(e.target.value)}>
            <option value="">All Kitchens</option>
            {kitchens.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
          </select>
          <button onClick={() => { setForm({ category: "Machinery" }); setModal({ mode: "create" }); setError(""); }}
            className="bg-[#E8956D] text-[#3A322C] rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-1.5">
            <Plus size={14} /> Add Asset
          </button>
        </div>
      </div>

      {success && <div className="bg-[#EDF2EE] border border-[#637C64]/30 rounded-lg px-4 py-2.5 text-sm text-[#637C64]">{success}</div>}

      {/* Alert banner */}
      {alerts.filter(a => a.critical).length > 0 && (
        <div className="bg-[#FBEAE7] border border-[#C2624E]/30 rounded-xl px-5 py-3 flex items-center gap-3">
          <AlertTriangle size={18} className="text-[#C2624E] flex-shrink-0" />
          <p className="text-sm font-bold text-[#C2624E]">
            {alerts.filter(a => a.critical).length} asset(s) have expired warranty or overdue service
          </p>
        </div>
      )}

      <div className="flex border-b border-[#E5E1DB]">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-4 py-3 text-sm font-semibold transition-colors -mb-px ${activeTab === t.id ? "border-b-2 border-[#E8956D] text-[#E8956D]" : "text-[#8A7E73] hover:text-[#5C5147]"}`}>
            {t.label}
            {t.id === "alerts" && alerts.length > 0 && (
              <span className="ml-2 text-[10px] bg-[#C2624E] text-white px-1.5 py-0.5 rounded-full font-bold">{alerts.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* Filters for register tab */}
      {activeTab === "register" && (
        <div className="flex gap-3 flex-wrap items-center">
          <div className="relative flex-1 min-w-48">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A7E73]" />
            <input placeholder="Search assets..." value={search} onChange={e => setSearch(e.target.value)}
              className="w-full border border-[#E5E1DB] rounded-lg pl-8 pr-3 py-2 text-sm outline-none focus:border-[#E8956D]" />
          </div>
          <select className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
            value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
            <option value="">All Categories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <button onClick={() => exportCSV(assets.map(a => ({ name: a.name, category: a.category, kitchen: kName(a.kitchen_id), serial: a.serial_number, purchase_date: a.purchase_date, warranty_expiry: a.warranty_expiry, service_provider: a.service_provider_name, next_service: a.next_service_date })), "assets")}
            className="flex items-center gap-1.5 text-sm border border-[#E5E1DB] rounded-lg px-3 py-2 text-[#5C5147] hover:bg-[#F4F3F0]">
            <Download size={14} /> Export
          </button>
        </div>
      )}

      {loading ? <div className="flex justify-center py-12"><div className="w-8 h-8 border-3 border-[#E8956D] border-t-transparent rounded-full animate-spin" /></div> : (
        <>
          {/* ASSET REGISTER */}
          {activeTab === "register" && (
            <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F3F0]">
                    <tr>{["Asset Name", "Category", "Kitchen", "Serial No.", "Purchase Date", "Warranty Expiry", "Service Provider", "Next Service", "Status", ""].map(h => (
                      <th key={h} className="text-left px-3 py-3 text-xs font-bold uppercase tracking-wider text-[#5C5147] whitespace-nowrap">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y divide-[#F4F3F0]">
                    {assets.map(a => (
                      <tr key={a.id} className={`hover:bg-[#FAFAF8] ${(a.warranty_alert || a.service_alert) ? "bg-[#FDF4EB]/30" : ""}`}>
                        <td className="px-3 py-3 font-medium text-[#3A322C]">{a.name}</td>
                        <td className="px-3 py-3"><span className="text-xs bg-[#F4F3F0] text-[#5C5147] px-2 py-0.5 rounded-full">{a.category}</span></td>
                        <td className="px-3 py-3 text-[#8A7E73]">{kName(a.kitchen_id)}</td>
                        <td className="px-3 py-3 text-[#8A7E73]">{a.serial_number || "—"}</td>
                        <td className="px-3 py-3 text-[#8A7E73] whitespace-nowrap">{a.purchase_date || "—"}</td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          {a.warranty_expiry ? (
                            <span className={`text-xs font-bold ${a.warranty_alert ? "text-[#C2624E]" : "text-[#637C64]"}`}>
                              {a.warranty_expiry} {a.warranty_days_left !== undefined && `(${a.warranty_days_left > 0 ? a.warranty_days_left + "d left" : "EXPIRED"})`}
                            </span>
                          ) : "—"}
                        </td>
                        <td className="px-3 py-3 text-[#8A7E73]">{a.service_provider_name || "—"}<br /><span className="text-xs">{a.service_provider_contact || ""}</span></td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          {a.next_service_date ? (
                            <span className={`text-xs font-bold ${a.service_alert ? "text-[#D98032]" : "text-[#5C5147]"}`}>
                              {a.next_service_date} {a.service_days_left !== undefined && `(${a.service_days_left > 0 ? a.service_days_left + "d" : "OVERDUE"})`}
                            </span>
                          ) : "—"}
                        </td>
                        <td className="px-3 py-3">
                          {(a.warranty_alert || a.service_alert)
                            ? <AlertTriangle size={14} className="text-[#D98032]" />
                            : <CheckCircle size={14} className="text-[#637C64]" />}
                        </td>
                        <td className="px-3 py-3"><div className="flex gap-2">
                          <button onClick={() => { setForm({ ...a }); setModal({ mode: "edit", id: a.id }); setError(""); }} className="text-[#8A7E73] hover:text-[#E8956D]"><Edit size={14} /></button>
                          <button onClick={() => handleDelete(a.id)} className="text-[#8A7E73] hover:text-[#C2624E]"><Trash2 size={14} /></button>
                        </div></td>
                      </tr>
                    ))}
                    {assets.length === 0 && <tr><td colSpan={10} className="text-center py-10 text-[#8A7E73]">No assets found</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ALERTS */}
          {activeTab === "alerts" && (
            <div className="space-y-3">
              {alerts.length === 0 ? (
                <div className="text-center py-16 text-[#637C64]"><CheckCircle size={48} className="mx-auto mb-3 opacity-30" /><p>No warranty or service alerts</p></div>
              ) : alerts.map((a, i) => (
                <div key={`idx-${i}`} className={`bg-white border rounded-xl p-4 flex items-center justify-between ${a.critical ? "border-[#C2624E]" : "border-[#D98032]"}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${a.critical ? "bg-[#FBEAE7]" : "bg-[#FDF4EB]"}`}>
                      {a.type === "warranty" ? <Wrench size={18} className={a.critical ? "text-[#C2624E]" : "text-[#D98032]"} /> : <CheckCircle size={18} className={a.critical ? "text-[#C2624E]" : "text-[#D98032]"} />}
                    </div>
                    <div>
                      <p className="font-bold text-[#3A322C]">{a.asset_name}</p>
                      <p className={`text-sm font-medium ${a.critical ? "text-[#C2624E]" : "text-[#D98032]"}`}>{a.message}</p>
                    </div>
                  </div>
                  <span className={`text-xs font-bold px-3 py-1.5 rounded-full uppercase ${a.critical ? "bg-[#FBEAE7] text-[#C2624E]" : "bg-[#FDF4EB] text-[#D98032]"}`}>
                    {a.type}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* MODAL */}
      {modal && (
        <Modal title={`${modal.mode === "create" ? "Add" : "Edit"} Asset`} onClose={() => setModal(null)}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Asset Name *</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Category *</label>
                <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.category || "Machinery"} onChange={e => setForm({ ...form, category: e.target.value })}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Kitchen *</label>
                <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.kitchen_id || filterKitchen || ""} onChange={e => setForm({ ...form, kitchen_id: e.target.value })}>
                  <option value="">Select Kitchen</option>{kitchens.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
                </select></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Serial Number</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.serial_number || ""} onChange={e => setForm({ ...form, serial_number: e.target.value })} /></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Model Number</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.model_number || ""} onChange={e => setForm({ ...form, model_number: e.target.value })} /></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Purchase Date</label><input type="date" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.purchase_date || ""} onChange={e => setForm({ ...form, purchase_date: e.target.value })} /></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Purchase Cost (₹)</label><input type="number" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.purchase_cost || ""} onChange={e => setForm({ ...form, purchase_cost: parseFloat(e.target.value) })} /></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Warranty Expiry</label><input type="date" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.warranty_expiry || ""} onChange={e => setForm({ ...form, warranty_expiry: e.target.value })} /></div>
              <div className="col-span-2"><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Service Provider Name</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.service_provider_name || ""} onChange={e => setForm({ ...form, service_provider_name: e.target.value })} /></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Service Provider Contact</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.service_provider_contact || ""} onChange={e => setForm({ ...form, service_provider_contact: e.target.value })} /></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Last Service Date</label><input type="date" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.last_service_date || ""} onChange={e => setForm({ ...form, last_service_date: e.target.value })} /></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Next Service Date</label><input type="date" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.next_service_date || ""} onChange={e => setForm({ ...form, next_service_date: e.target.value })} /></div>
              <div className="col-span-2"><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Notes</label><textarea rows={2} className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D] resize-none" value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            {error && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</p>}
            <div className="flex gap-3">
              <button onClick={() => setModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
              <button onClick={handleSave} className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C]">Save Asset</button>
            </div>
          </div>
        </Modal>
      )}
      {ConfirmModal}
    </div>
  );
}
